import { buildSolutionCardCanvas } from "./solutionsCardTexture";
import { loadSolutionCardTemplate } from "./solutionCardModelCache";

// THREE vient du bundle-three-vendor (cf. app/js/ThreeVendor.js), même principe que
// SolutionsCardsScene : une seule copie de Three.js partagée entre bundle-home/wave/solutions.
const { THREE } = window.ThreeVendor;

// Contrairement à SolutionsCardsScene (anneau serré de plusieurs cartes, cadrage large), ici une
// seule carte doit remplir un container plus vertical (4/5) : caméra plus proche pour qu'elle
// occupe bien la largeur, tout en gardant CARD_SCALE pour ne pas rogner le haut/bas de la carte.
const CAMERA_FOV = 30;
const CAMERA_DISTANCE = 4.2;
const CARD_SCALE = 0.8;
const MAX_TILT_PITCH = 0.286;
const MAX_TILT_YAW = 0.416;
const EASE = 0.12;

// Une seule carte du modèle GLB "solution-card.glb", statique tant qu'elle n'est pas survolée :
// contrairement à SolutionsCardsScene (anneau de cartes, rotation continue ou pilotée par un
// Swiper), ici chaque carte de la grille d'archive a sa propre scène/caméra/renderer et ne réagit
// qu'au survol local de son propre canvas (tilt uniquement — une translation ferait sortir la
// carte du cadre du canvas, cf. le cadrage serré en largeur).
export default class SolutionCardHoverScene {
	constructor({ canvas, card, modelUrl }) {
		this.canvas = canvas;
		this.card = card;
		this.modelUrl = modelUrl;

		this.tiltPitch = 0;
		this.tiltYaw = 0;
		this.targetPitch = 0;
		this.targetYaw = 0;

		this._buildScene();
	}

	_buildScene() {
		this.scene = new THREE.Scene();

		const rect = this.canvas.getBoundingClientRect();
		this.camera = new THREE.PerspectiveCamera(CAMERA_FOV, rect.width / Math.max(rect.height, 1), 0.1, 100);
		this.camera.position.set(0, 0, CAMERA_DISTANCE);
		this.camera.lookAt(0, 0, 0);

		this.renderer = new THREE.WebGLRenderer({ canvas: this.canvas, antialias: true, alpha: true });
		this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
		this.renderer.setSize(rect.width, Math.max(rect.height, 1), false);
		this.renderer.outputColorSpace = THREE.SRGBColorSpace;

		this.scene.add(new THREE.AmbientLight(0xffffff, 1.7));
		const sun = new THREE.DirectionalLight(0xffffff, 1.3);
		sun.position.set(2, 4, 4);
		this.scene.add(sun);

		this.holder = new THREE.Group();
		this.scene.add(this.holder);
	}

	async load() {
		const template = await loadSolutionCardTemplate(this.modelUrl);
		const cardScene = template.clone(true);
		// Object3D.clone() partage les matériaux par référence : on les clone pour de bon.
		cardScene.traverse((node) => {
			if (node.isMesh && node.material) {
				node.material = node.material.clone();
			}
		});

		const canvas = await buildSolutionCardCanvas(this.card);
		const texture = new THREE.CanvasTexture(canvas);
		texture.colorSpace = THREE.SRGBColorSpace;
		texture.flipY = false;
		texture.needsUpdate = true;

		cardScene.traverse((node) => {
			if (node.isMesh && node.material && node.material.name === "logo_zone") {
				node.material.map = texture;
				node.material.side = THREE.FrontSide;
				node.material.needsUpdate = true;
			}
		});

		cardScene.scale.setScalar(CARD_SCALE);

		// Le pivot du GLB n'est pas centré verticalement (la carte apparaissait décalée vers le
		// haut du cadre, avec un vide plus grand en bas) : on recentre sur sa propre bounding box,
		// ce qui a aussi l'avantage de faire pivoter la carte autour de son centre visuel au survol.
		const box = new THREE.Box3().setFromObject(cardScene);
		const center = box.getCenter(new THREE.Vector3());
		cardScene.position.y -= center.y;

		this.holder.add(cardScene);
	}

	setHoverTilt(x, y) {
		this.targetYaw = x * MAX_TILT_YAW;
		this.targetPitch = -y * MAX_TILT_PITCH;
	}

	resetTilt() {
		this.targetYaw = 0;
		this.targetPitch = 0;
	}

	update() {
		this.tiltPitch += (this.targetPitch - this.tiltPitch) * EASE;
		this.tiltYaw += (this.targetYaw - this.tiltYaw) * EASE;

		this.holder.rotation.set(this.tiltPitch, this.tiltYaw, 0);

		this.renderer.render(this.scene, this.camera);
	}

	resize() {
		const rect = this.canvas.getBoundingClientRect();
		this.camera.aspect = rect.width / Math.max(rect.height, 1);
		this.camera.updateProjectionMatrix();
		this.renderer.setSize(rect.width, Math.max(rect.height, 1), false);
	}

	destroy() {
		this.renderer.dispose();
		// La géométrie vient du template mis en cache (solutionCardModelCache) et est partagée par
		// tous les clones : ne jamais la dispose ici, une carte encore visible la référence toujours.
		// Seuls le matériau (cloné par carte) et sa texture (canvas composé par carte) sont propres
		// à cette instance.
		this.holder.traverse((node) => {
			if (node.isMesh && node.material) {
				if (node.material.map) node.material.map.dispose();
				node.material.dispose();
			}
		});
	}
}
